import { useCallback, useEffect, useRef, useState } from 'react';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_DURATION_S = 60; // 60 second countdown
const THROTTLE_MS = 5000; // Only process activity events once per 5 seconds

interface UseIdleTimeoutReturn {
  showWarning: boolean;
  countdown: number;
  stayLoggedIn: () => void;
}

export function useIdleTimeout(
  onTimeout: () => void,
  onStayLoggedIn: () => Promise<void>,
  isAuthenticated: boolean
): UseIdleTimeoutReturn {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_DURATION_S);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  // Use ref for showWarning so event listeners always see the latest value
  // without needing to be re-registered on every state change
  const showWarningRef = useRef(false);
  const lastActivityRef = useRef(0);
  // Track timestamps so we use wall-clock time instead of relying on
  // setInterval/setTimeout tick accuracy (browsers throttle these in background tabs)
  const warningStartRef = useRef(0);
  const idleStartRef = useRef(0);

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const startWarningCountdown = useCallback(() => {
    setShowWarning(true);
    showWarningRef.current = true;
    setCountdown(WARNING_DURATION_S);
    warningStartRef.current = Date.now();

    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - warningStartRef.current) / 1000);
      const remaining = WARNING_DURATION_S - elapsed;

      if (remaining <= 0) {
        clearTimers();
        setShowWarning(false);
        showWarningRef.current = false;
        setCountdown(0);
        onTimeout();
      } else {
        setCountdown(remaining);
      }
    }, 1000);
  }, [clearTimers, onTimeout]);

  const resetIdleTimer = useCallback(() => {
    clearTimers();
    setShowWarning(false);
    showWarningRef.current = false;
    setCountdown(WARNING_DURATION_S);
    idleStartRef.current = Date.now();

    idleTimerRef.current = setTimeout(startWarningCountdown, IDLE_TIMEOUT_MS);
  }, [clearTimers, startWarningCountdown]);

  const stayLoggedIn = useCallback(async () => {
    clearTimers();
    setShowWarning(false);
    showWarningRef.current = false;
    setCountdown(WARNING_DURATION_S);

    try {
      await onStayLoggedIn();
    } catch {
      // If refresh fails, the next API call will handle it
    }

    resetIdleTimer();
  }, [clearTimers, onStayLoggedIn, resetIdleTimer]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      setShowWarning(false);
      showWarningRef.current = false;
      return;
    }

    const handleActivity = () => {
      if (showWarningRef.current) return;

      const now = Date.now();
      if (now - lastActivityRef.current < THROTTLE_MS) return;
      lastActivityRef.current = now;

      resetIdleTimer();
    };

    // When the tab becomes visible again, check if timers should have fired
    // while the tab was in the background (browsers throttle timers in background tabs)
    const handleVisibilityChange = () => {
      if (document.hidden) return;

      if (showWarningRef.current) {
        // Recalculate countdown from wall-clock time
        const elapsed = Math.floor(
          (Date.now() - warningStartRef.current) / 1000
        );
        const remaining = WARNING_DURATION_S - elapsed;

        if (remaining <= 0) {
          clearTimers();
          setShowWarning(false);
          showWarningRef.current = false;
          setCountdown(0);
          onTimeout();
        } else {
          setCountdown(remaining);
        }
      } else {
        // Check if idle timeout should have triggered while tab was hidden
        const idleElapsed = Date.now() - idleStartRef.current;
        if (idleElapsed >= IDLE_TIMEOUT_MS) {
          clearTimers();
          startWarningCountdown();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Start initial timer
    resetIdleTimer();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearTimers();
    };
  }, [
    isAuthenticated,
    resetIdleTimer,
    clearTimers,
    onTimeout,
    startWarningCountdown,
  ]);

  return { showWarning, countdown, stayLoggedIn };
}
